import { CellValueChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { MdOutlineContentCopy } from "react-icons/md";
import { useSplashMessage } from "src/hook/useSplashMessage";
import {
  QuotationDetailInitialValueParam,
  QuotationDetailInitialValueParamSub,
} from "src/model/param/QuotationDetailInitialValueParam";

import { useAuthUserContext } from "../../context/AuthUser";
import { useDialog } from "../../context/DialogProvider";
import useApi from "../../hook/useApi";
import { QuotationsPostRequest } from "../../model/api/request/QuotationsPostRequest";
import { QuotationsPostRequestSub } from "../../model/api/request/QuotationsPostRequestSub";
import { OrderGetResponse } from "../../model/api/response/OrderGetResponse";
import { OrderGetResponseSub } from "../../model/api/response/OrderGetResponseSub";
import { ProductGetResponse } from "../../model/api/response/ProductGetResponse";
import { ProductsGetResponse } from "../../model/api/response/ProductsGetResponse";
import { QuotationGetResponse } from "../../model/api/response/QuotationGetResponse";
import { QuotationGetResponseSub } from "../../model/api/response/QuotationGetResponseSub";
import {
  MaintenanceScheduleKey,
  QuotationDetailFormRowValues,
  QuotationDetailFormValues,
  QuotationDetailHandles,
  QuotationDetailSaveButtonName,
  QuotationDetailShowParam,
  RecommendKey,
} from "../../model/form/QuotationDetailForm";
import { defaultColDef } from "../../util/AgGridUtil";
import {
  OrderFactor,
  OrderStatus,
  ProductCodeConst,
  QuotationStatus,
} from "../../util/Constant";
import { calculateTax } from "../../util/TaxUtil";
import { formatDate, isEnglish } from "../../util/ecUtil";
import S3Uploader, { S3UploaderHandles } from "../common/S3Uploader";
import ProductListModal, {
  ProductListModalHandles,
} from "../setting/ProductListModal";
import OrderDetail, { OrderDetailHandles, OrderParam } from "./OrderDetail";
import OrderDetailDelivery from "./OrderDetailDelivery";
import OrderDetailDiscountRate from "./OrderDetailDiscountRate";
import OrderDetailHeader, {
  OrderDetailChangeEvent,
  OrderDetailType,
} from "./OrderDetailHeader";
import OrderDetailPrice from "./OrderDetailPrice";
import {
  calculateReplyPriceCore,
  canAddExpensesRow,
  copyDetailsToClipboard,
  isEmptyQuotationRow,
  useDetailRowDrag,
} from "./OrderQuotationUtil";
import QuotationDetailFooter from "./QuotationDetailFooter";
import { buildQuotationDetailColumnDefs } from "./QuotationDetailGrid";
import { createQuotationDetailPermission } from "./QuotationDetailPermission";

// 呼び出し元との互換のため型を再公開する
export type { QuotationDetailHandles };

type Props = {
  onClickSaveButton?: (buttonName: QuotationDetailSaveButtonName) => void;
  quotationOrderHistoryFlg?: boolean;
};

// エリアごとのCSSクラス
const areaClass = "p-1 bg-white b-detail-items";
const textAreaClass =
  "form-control textarea b-input-text-square b-input-comment-height";

/** 見積詳細コンポーネント */
const QuotationDetail = forwardRef<QuotationDetailHandles, Props>(
  (props: Props, ref) => {
    // ダイアログ使用宣言
    const showDialog = useDialog();
    //スプラッシュメッセージ使用宣言
    const splashMessage = useSplashMessage();
    // API使用宣言
    const api = useApi();
    // ユーザ情報
    const auth = useAuthUserContext();
    // 翻訳
    const { t: tc } = useTranslation("QuotationDetail");

    // state宣言
    const [quotationNo, setQuotationNo] = useState<number | undefined>();
    const [maintenanceScheduleKey, setMaintenanceScheduleKey] = useState<
      MaintenanceScheduleKey | undefined
    >();
    const [recommendKey, setRecommendKey] = useState<
      RecommendKey | undefined
    >();
    const [show, setShow] = useState(false);

    //クリップボードにコピー関数
    const copyToClipboard = () =>
      copyDetailsToClipboard(
        inpDetails,
        [
          "No",
          "品目コード",
          "品名",
          "型式",
          "単価",
          "依頼数量",
          "単位",
          "小計",
          "明細備考",
          "単価(BEMAC回答)",
          "数量(BEMAC回答)",
          "小計(BEMAC回答)",
        ],
        (inpData) => [
          inpData.quotationDetailNo ?? "",
          inpData.productCode ?? "",
          isEnglish()
            ? inpData.productGroupNameEn
            : inpData.productGroupName ?? "",
          inpData.spec ?? "",
          inpData.standardUnitPrice ?? "",
          inpData.quantity ?? "",
          inpData.unit ?? "",
          inpData.detailSubTotal ?? "",
          inpData.note ?? "",
          inpData.replyUnitCost ?? "",
          inpData.replyQuantity ?? "",
          inpData.replyDetailSubTotal ?? "",
        ]
      );

    // 入力項目
    const [inpQuotation, setInpQuotation] = useState<QuotationDetailFormValues>(
      generateDraftQuotation()
    );
    // 入力項目：明細
    const [inpDetails, setInpDetails] = useState<
      QuotationDetailFormRowValues[]
    >([]);

    // ユーザ入力有無
    const [formChanged, setFormChanged] = useState<boolean>(false);
    // 読み取り専用フラグ
    const [readOnly, setReadOnly] = useState(false);
    // 参照
    const gridRef = useRef<AgGridReact>(null); // 明細
    const productListModalRef = useRef<ProductListModalHandles>(null); // 商品一覧
    const uploaderCustomerRef = useRef<S3UploaderHandles>(null); // ファイル添付（顧客）
    const uploaderBemacRef = useRef<S3UploaderHandles>(null); // ファイル添付（BEMAC）
    const uploaderFreeMemo = useRef<S3UploaderHandles>(null); // ファイル添付（フリーメモ）
    const orderDetailRef = useRef<OrderDetailHandles>(null); // 注文詳細
    const [currentRegularPriceFlg, setCurrentRegularPriceFlg] =
      useState<boolean>(false); // 定価フラグ

    // 表示・編集可否判定（ステータス×ユーザ種別×見積種別）
    const permission = createQuotationDetailPermission({
      inpQuotation,
      inpDetails,
      readOnly,
      auth,
    });
    const {
      canAddNewRow,
      isLcm,
      isGeneralEngineer,
      editableFileUpload,
      editableComment,
      showsTempPrice,
      showsUserCommentDraft,
      showsUserCommentDenied,
    } = permission;

    function generateDraftQuotation(): QuotationDetailFormValues {
      return {
        statusClassValue: QuotationStatus.DRAFT,
        quotationClassValue: OrderFactor.EC_PARTS,
        companyCode: auth.user().bemacFlg ? "" : auth.user().companyCode,
        companyName: auth.user().bemacFlg ? "" : auth.user().companyName,
        imoNo: "",
        vesselName: "",
        vesselBuilder: "",
        yardNo: "",
        requestDeliveryDate: "",
        customerQuotationNo1: "",
        customerQuotationNo2: "",
        requestReplyDeadline: "",
        bemacStartFlg: auth.user().bemacFlg,
        deliveryName: "",
        deliveryPostalCode: "",
        deliveryAddress: "",
        deliveryTel: "",
        dispatchFlg: false,
        dispatchPlace: "",
        dispatchDate: "",
        quotationUserId: auth.user().bemacFlg ? undefined : auth.user().userId,
        quotationUserName: auth.user().bemacFlg
          ? undefined
          : auth.user().userName,
        quotationComment: "",
        quotationDateTime: "",
        replyUserId: auth.user().bemacFlg ? auth.user().userId : undefined,
        replyUserName: auth.user().bemacFlg ? auth.user().userName : undefined,
        replyComment: "",
        replyDateTime: "",
        replyDeliveryDate: "",
        replyInstallmentDeliveryFlg: false,
        tempSubtotalPrice: 0,
        replySubtotalPrice: undefined,
        replyDeliveryPrice: undefined,
        replyDiscountPrice: 0,
        replyTax: 0,
        replyTaxPrice: undefined,
        replyTotalPrice: undefined,
        discountRate: undefined,
        checkUserId: undefined,
        checkUserName: undefined,
        checkDateTime: "",
        updateDateTime: "",
        dockName: "",
        dockScheduleStart: "",
        dockScheduleEnd: "",
        s3Prefix: generateS3Prefix(),
        problemProduct: undefined,
        problemDetail: undefined,
        reQuotationFlg: false,
        bemacFreeComment: "",
      };
    }

    // S3に登録するオブジェクトキーのプリフィクスを採番
    function generateS3Prefix() {
      const datetime = formatDate(new Date(), "YYYYMMDDHHmmss");
      return `order/${datetime}-${auth.user().userId}/`;
    }

    // 公開する関数の実装
    useImperativeHandle(ref, () => ({
      show(param: QuotationDetailShowParam) {
        // 初期化
        setFormChanged(false);
        setQuotationNo(param.quotationNo);
        setMaintenanceScheduleKey(param.maintenanceScheduleKey);
        setRecommendKey(param.recommendKey);
        if (param.quotationNo) {
          // 見積番号が設定されていたらAPIから初期化する
          api
            .get<QuotationGetResponse>(
              `/api/v1/quotations/${param.quotationNo}`
            )
            .then((response) => {
              const newQuotation = mapQuotationGetResponseToFormValues(
                response.data
              );

              // LCMの場合再見積依頼時に更新明細値がわたってくるので、セットする
              if (
                response.data.quotationClassValue === OrderFactor.LCM_PARTS &&
                param.initialData
              ) {
                const initialDetails = param.initialData?.details ?? [];
                // 更新値で渡されている品目の情報を取得しておく
                getProductDatas(initialDetails).then((productDatas) => {
                  // 明細初期値（渡されていなければ0行の配列）
                  const detailRows: QuotationDetailFormRowValues[] =
                    mapInitialValueParamSubToFormRowValues(
                      initialDetails,
                      productDatas
                    );
                  // stateに保存してモーダルを表示状態にする
                  saveInitialDataAndShow(newQuotation, detailRows);
                });
              } else {
                const detailRows = mapQuotationGetResponseSubToFormRowValues(
                  response.data.quotationDetail,
                  true
                );
                // stateに保存してモーダルを表示状態にする
                saveInitialDataAndShow(newQuotation, detailRows);
              }
            })
            .catch((error) => showDialog({ error }));
        } else {
          const initialDetails = param.initialData?.details ?? [];
          // 初期値で渡されている品目の情報を取得しておく
          getProductDatas(initialDetails).then((productDatas) => {
            // 新規作成の場合、デフォルト値を設定
            const newQuotation = generateDraftQuotation();
            // 見積種別区分コードを設定
            if (param.maintenanceScheduleKey) {
              newQuotation.quotationClassValue = OrderFactor.LCM_PARTS;
            } else if (param.recommendKey) {
              newQuotation.quotationClassValue = OrderFactor.LCM_RECOMMEND;
            }
            // 初期値が渡されていたら設定する
            if (param.initialData) {
              setFromInitalData(newQuotation, param.initialData);
            }
            // 明細初期値（渡されていなければ0行の配列）
            const detailRows: QuotationDetailFormRowValues[] =
              mapInitialValueParamSubToFormRowValues(
                initialDetails,
                productDatas
              );
            // stateに保存してモーダルを表示状態にする
            saveInitialDataAndShow(newQuotation, detailRows);
          });
        }
      },
    }));

    /** 見積取得APIのレスポンスをFormValuesに変換 */
    function mapQuotationGetResponseToFormValues(src: QuotationGetResponse) {
      const mappedQuotation: QuotationDetailFormValues = {
        statusClassValue: src.statusClassValue,
        quotationClassValue: src.quotationClassValue ?? undefined,
        companyCode: src.companyCode,
        companyName: src.companyName ?? undefined,
        imoNo: src.imoNo,
        vesselName: src.vesselName ?? undefined,
        vesselBuilder: src.vesselBuilder ?? undefined,
        yardNo: src.yardNo ?? undefined,
        requestDeliveryDate: src.requestDeliveryDate ?? undefined,
        customerQuotationNo1: src.customerQuotationNo1 ?? undefined,
        customerQuotationNo2: src.customerQuotationNo2 ?? undefined,
        requestReplyDeadline: src.requestReplyDeadline ?? undefined,
        bemacStartFlg: src.bemacStartFlg,
        deliveryName: src.deliveryName,
        deliveryPostalCode: src.deliveryPostalCode,
        deliveryAddress: src.deliveryAddress,
        deliveryTel: src.deliveryTel,
        dispatchFlg: src.dispatchFlg,
        dispatchPlace: src.dispatchPlace ?? undefined,
        dispatchDate: src.dispatchDate ?? undefined,
        quotationUserId: src.quotationUserId,
        quotationUserName: src.quotationUserName,
        quotationComment: src.quotationComment ?? undefined,
        quotationDateTime: src.quotationDateTime ?? undefined,
        replyUserId: src.replyUserId ?? undefined,
        replyUserName: src.replyUserName ?? undefined,
        replyComment: src.replyComment ?? undefined,
        replyDateTime: src.replyDateTime ?? undefined,
        replyDeliveryDate: src.replyDeliveryDate ?? undefined,
        replyInstallmentDeliveryFlg: src.replyInstallmentDeliveryFlg,
        tempSubtotalPrice: src.tempSubtotalPrice,
        replySubtotalPrice: src.replySubtotalPrice ?? undefined,
        replyDeliveryPrice: src.replyDeliveryPrice ?? undefined,
        replyDiscountPrice: src.replyDiscountPrice ?? undefined,
        replyTax: src.replyTax ?? undefined,
        replyTaxPrice: src.replyTaxPrice ?? undefined,
        replyTotalPrice: src.replyTotalPrice ?? undefined,
        discountRate: src.discountRate ?? undefined,
        checkUserId: src.checkUserId ?? undefined,
        checkUserName: src.checkUserName ?? undefined,
        checkDateTime: src.checkDateTime ?? undefined,
        // s3Prefixが付いてなければ付けなおす
        s3Prefix: src.s3Prefix ?? generateS3Prefix(),
        updateDateTime: src.updateDateTime ?? undefined,
        dockName: src.dockName ?? undefined,
        dockScheduleStart: src.dockScheduleStart ?? undefined,
        dockScheduleEnd: src.dockScheduleEnd ?? undefined,
        problemProduct: src.problemProduct ?? undefined,
        problemDetail: src.problemDetail ?? undefined,
        reQuotationFlg: false,
        bemacFreeComment: src.bemacFreeComment ?? undefined,
      };
      return mappedQuotation;
    }

    /** 見積取得APIのレスポンスをFormRowValuesに変換 */
    function mapQuotationGetResponseSubToFormRowValues(
      src: QuotationGetResponseSub[],
      isDb: boolean
    ) {
      const mappedRows: QuotationDetailFormRowValues[] = [];
      src.forEach((it, index) => {
        mappedRows.push({
          key: index,
          isDbRow: isDb,
          quotationDetailNo: it.quotationDetailNo,
          productCode: it.productCode,
          productGroupName: it.productGroupName,
          productGroupNameEn: it.productGroupNameEn,
          spec: it.spec ?? undefined,
          standardUnitPrice: it.standardUnitPrice ?? undefined,
          quantity: it.quantity,
          unit: it.unit ?? undefined,
          note: it.note ?? undefined,
          replyUnitCost: it.replyUnitCost ?? undefined,
          replyQuantity: it.replyQuantity ?? undefined,
          replyDeliveryDate: it.replyDeliveryDate ?? undefined,
          replyDetailSubTotal: it.replyDetailSubTotal ?? undefined,
          detailSubTotal: (it.standardUnitPrice ?? 0) * (it.quantity ?? 0),
          referenceDetailNo: it.referenceDetailNo ?? undefined,
          createUserId: it.createUserId ?? undefined,
          createDateTime: it.createDateTime ?? undefined,
          updateUserId: it.updateUserId ?? undefined,
          updateDateTime: it.updateDateTime ?? undefined,
          addsByBemac: it.addsByBemac,
          existFlg: true,
        });
      });
      return mappedRows;
    }

    /** 遷移パラメータの初期値をFormValuesに上書きする */
    function setFromInitalData(
      newQuotation: QuotationDetailFormValues,
      initialData: QuotationDetailInitialValueParam
    ) {
      // 得意先コード
      newQuotation.companyCode =
        initialData.companyCode ?? newQuotation.companyCode;
      // 得意先名
      newQuotation.companyName =
        initialData.companyName ?? newQuotation.companyName;
      // IMO
      newQuotation.imoNo = initialData.imoNo ?? newQuotation.imoNo;
      // 船名
      newQuotation.vesselName =
        initialData.vesselName ?? newQuotation.vesselName;
      // 建造造船所
      newQuotation.vesselBuilder =
        initialData.vesselBuilder ?? newQuotation.vesselBuilder;
      // 造船所番号
      newQuotation.yardNo = initialData.yardNo ?? newQuotation.yardNo;
      // 技師派遣フラグ
      newQuotation.dispatchFlg =
        initialData.dispatchFlg ?? newQuotation.dispatchFlg;
      // 入渠ドック名
      newQuotation.dockName = initialData.dockName ?? newQuotation.dockName;
      // 入渠ドック予定開始日
      newQuotation.dockScheduleStart =
        initialData.dockScheduleStart ?? newQuotation.dockScheduleStart;
      // 入渠ドック予定終了日
      newQuotation.dockScheduleEnd =
        initialData.dockScheduleEnd ?? newQuotation.dockScheduleEnd;
    }

    /** 遷移パラメータの初期値をFormRowValuesに変換 */
    function mapInitialValueParamSubToFormRowValues(
      src: QuotationDetailInitialValueParamSub[],
      productDatas: ProductGetResponse[]
    ) {
      const mappedRows: QuotationDetailFormRowValues[] = [];
      src.forEach((it, index) => {
        // 商品情報を特定
        const productData = productDatas.find(
          (data) => data.productCode === it.productCode
        );

        mappedRows.push({
          key: index,
          isDbRow: false,
          quotationDetailNo: index + 1,
          productCode: it.productCode,
          productGroupName: it.productGroupName
            ? it.productGroupName
            : productData?.productGroupName,
          productGroupNameEn: it.productGroupNameEn
            ? it.productGroupNameEn
            : productData?.productGroupNameEn,
          spec: it.spec ? it.spec : productData?.spec,
          standardUnitPrice: productData?.unitPrice,
          quantity: undefined,
          unit: productData?.unit,
          note: productData?.note,
          replyUnitCost: it.unitPrice ?? productData?.unitPrice,
          replyQuantity: it.quantity,
          replyDeliveryDate: undefined,
          replyDetailSubTotal: it.unitPrice
            ? (it.unitPrice ?? 0) * (it.quantity ?? 0)
            : (productData?.unitPrice ?? 0) * (it.quantity ?? 0),
          detailSubTotal: (productData?.unitPrice ?? 0) * (it.quantity ?? 0),
          referenceDetailNo: it.referenceDetailNo,
          addsByBemac: auth.user().bemacFlg,
          existFlg: true,
        });
      });
      return mappedRows;
    }

    /** 商品情報を複数取得 */
    async function getProductDatas(
      details: QuotationDetailInitialValueParamSub[]
    ) {
      // 品目コードを収集（空文字、空行コードは除く）
      const productCodes = details
        .filter(
          (it) =>
            it.productCode.length > 0 &&
            it.productCode !== ProductCodeConst.BLANK
        )
        .map((it) => it.productCode);
      // 重複を除く
      const distinctProductCodes = Array.from(new Set(productCodes));
      const promises = distinctProductCodes.map((productCode) =>
        api
          .get<ProductGetResponse>(`/api/v1/products/${productCode}`)
          .then((it) => {
            return it.data;
          })
          .catch((error) => {
            if (error.response.status === 404) {
              return undefined;
            }
            throw error;
          })
      );
      const datas = await Promise.all(promises);
      // undefinedを取り除いて返す
      return datas.filter(
        (it): it is Exclude<typeof it, undefined> => typeof it !== "undefined"
      );
    }

    // stateに保存してモーダルを表示状態にする
    function saveInitialDataAndShow(
      quotation: QuotationDetailFormValues,
      detailRows: QuotationDetailFormRowValues[]
    ) {
      // stateに保存
      setInpQuotation(quotation);

      // 行追加可能時は新規行を追加
      // 技師以外で作成中・依頼中は追加可能
      // LCMの場合はBEMACのみ可（諸経費入力のため）
      if (
        !auth.isEngineer() &&
        [QuotationStatus.DRAFT, QuotationStatus.REQUESTED].includes(
          quotation.statusClassValue
        ) &&
        (quotation.quotationClassValue !== OrderFactor.LCM_PARTS ||
          auth.user().bemacFlg)
      ) {
        //Bemacユーザで技師派遣フラグONかつ(依頼中またはLCM/レコメンドから遷移時)
        //かつ、経費行が存在しない場合空白行と経費行を追加する
        if (
          auth.user().bemacFlg &&
          quotation.dispatchFlg &&
          (QuotationStatus.REQUESTED === quotation.statusClassValue ||
            (quotation.quotationClassValue &&
              [OrderFactor.LCM_PARTS, OrderFactor.LCM_RECOMMEND].includes(
                quotation.quotationClassValue
              ))) &&
          canAddExpensesRow(detailRows)
        ) {
          if (canAddReadyRow([...inpDetails], quotation.quotationClassValue)) {
            addNewRow(detailRows, [ProductCodeConst.BLANK, "", ""]);
            setInpDetails(detailRows);
            addReadyRow();
            addExpensesRow();
          } else {
            addNewRow(detailRows, [ProductCodeConst.BLANK, ""]);
            setInpDetails(detailRows);
            addExpensesRow();
          }
        } else {
          addNewRow(detailRows);
          setInpDetails(detailRows);
        }
      } else {
        setInpDetails(detailRows);
      }

      // 仮見積金額再計算
      calculateTempPrice(detailRows);
      // 作成中、依頼中は金額計算
      if (
        [QuotationStatus.DRAFT, QuotationStatus.REQUESTED].includes(
          quotation.statusClassValue
        )
      ) {
        calculateReplyPrice(detailRows);
      }

      // 読み取り専用モードの設定
      setReadOnlyMode(
        quotation.statusClassValue,
        quotation.companyCode,
        quotation.quotationUserId
      );

      // 表示
      setShow(true);
    }

    // 読み取り専用モードの設定
    function setReadOnlyMode(
      statusClassValue: string,
      companyCode: string,
      quotationUserId: number | undefined
    ) {
      // 技師は読み取り専用
      if (auth.isEngineer()) {
        setReadOnly(true);
        return;
      }

      // 成約、取消、否成約は読み取り専用
      if (
        [
          QuotationStatus.ACCEPTED,
          QuotationStatus.DELETED,
          QuotationStatus.DENIED,
        ].includes(statusClassValue)
      ) {
        setReadOnly(true);
        return;
      }

      // 顧客の場合は管理者か自身が依頼者でなければ読み取り専用
      if (
        !auth.user().bemacFlg &&
        quotationUserId &&
        auth.user().userId !== quotationUserId &&
        !auth.isCustomerAdmin()
      ) {
        setReadOnly(true);
        return;
      }

      // その他は編集可能
      setReadOnly(false);
    }

    // モーダル表示ハンドラ
    const handleShow = () => {
      // アップローダーの初期化
      uploaderCustomerRef.current?.init(
        `${inpQuotation.s3Prefix}customer/`,
        false
      );
      uploaderBemacRef.current?.init(`${inpQuotation.s3Prefix}bemac/`, false);
      uploaderFreeMemo.current?.init(
        `${inpQuotation.s3Prefix}free-memo/`,
        false
      );
    };

    // モーダルクローズハンドラ
    const handleClose = () => {
      if (formChanged) {
        showDialog({
          id: "I001",
          confirm: true,
          callback(isOk) {
            if (isOk) {
              setInpDetails([]);
              setShow(false);
              setCurrentRegularPriceFlg(false);
            }
          },
        });
      } else {
        setInpDetails([]);
        setShow(false);
        setCurrentRegularPriceFlg(false);
      }
    };

    // 保存系ボタン(メッセージ)
    function handleClickActionButton(name: QuotationDetailSaveButtonName) {
      // 明細の編集を停止
      gridRef.current?.api.stopEditing();

      if (name === "見積注文") {
        // 注文画面を表示
        const orderDetails = inpQuotationDetailToOrder();
        const order = inpQuotationToOrder(orderDetails);
        orderDetailRef.current?.show({
          quotationData: order,
          customerQuotationNo1: inpQuotation.customerQuotationNo1,
          customerQuotationNo2: inpQuotation.customerQuotationNo2,
        });
        return;
      }

      if (name === "見積依頼" && inpQuotation.requestReplyDeadline) {
        const currentDateTime = parseInt(formatDate(new Date(), "YYYYMMDD"));
        const requestReplyDeadline: number = parseInt(
          inpQuotation.requestReplyDeadline?.replaceAll(/-/g, "")
        );
        if (requestReplyDeadline < currentDateTime + 2) {
          // 確認ダイアログを表示
          showDialog({
            id: "E033",
            args: [tc("見積依頼")],
            confirm: true,
            callback(isOk) {
              if (isOk) {
                saveData(name);
              }
            },
          });
        } else {
          showConfirmMsg(name);
        }
      } else if (
        name === "見積回答" &&
        (inpQuotation.replyDiscountPrice === 0 || inpQuotation.replyTax === 0)
      ) {
        // 割引額もしくは消費税額が0だったら確認メッセージを表示
        showDialog({
          id: "E085",
          args: ["見積回答"],

          confirm: true,
          callback(isOk) {
            if (isOk) {
              showConfirmMsg(name);
            }
          },
        });
      } else {
        showConfirmMsg(name);
      }
    }

    function showConfirmMsg(name: QuotationDetailSaveButtonName) {
      // 確認メッセージ
      const messageId = confirmMessageId(name);
      if (messageId) {
        showDialog({
          id: messageId,
          confirm: true,
          callback: (isOk) => {
            if (isOk) {
              if (name === "再見積依頼") {
                // モード切替
                setInpQuotation((prev) => ({
                  ...prev,
                  reQuotationFlg: true,
                  statusClassValue: QuotationStatus.DRAFT,
                  quotationDateTime: undefined,
                }));
                setInpDetails((prev) => {
                  const rows = prev.map((row) => {
                    return {
                      ...row,
                      createUserId: undefined,
                      createDateTime: undefined,
                      updateUserId: undefined,
                      updateDateTime: undefined,
                      addsByBemac: auth.user().bemacFlg,
                      standardUnitPrice:
                        ProductCodeConst.DELIVERY_PRICE_CODES.includes(
                          row.productCode ?? ""
                        )
                          ? row.replyUnitCost
                          : row.standardUnitPrice,
                      quantity: ProductCodeConst.DELIVERY_PRICE_CODES.includes(
                        row.productCode ?? ""
                      )
                        ? row.replyQuantity
                        : row.quantity,
                      detailSubTotal:
                        ProductCodeConst.DELIVERY_PRICE_CODES.includes(
                          row.productCode ?? ""
                        )
                          ? row.replyDetailSubTotal
                          : row.detailSubTotal,
                    };
                  });
                  if (
                    OrderFactor.LCM_PARTS !== inpQuotation.quotationClassValue
                  ) {
                    // LCM以外は明細に入力用の新規行追加
                    addNewRow(rows);
                  }
                  // 仮見積金額再計算
                  calculateTempPrice(rows);
                  // 合計金額計算
                  calculateReplyPrice(rows);
                  return rows;
                });
              } else {
                saveData(name);
              }
            }
          },
        });
      } else {
        // メッセージなしで保存
        saveData(name);
      }
    }

    function inpQuotationDetailToOrder(): OrderGetResponseSub[] {
      return inpDetails
        .filter((it) => it.productCode)
        .map((row) => {
          return {
            orderDetailNo: row.quotationDetailNo,
            productCode: row.productCode ?? "",
            productGroupName: row.productGroupName ?? null,
            productGroupNameEn: row.productGroupNameEn ?? null,
            spec: row.spec ?? null,
            unit: row.unit ?? null,
            note: row.note ?? null,
            quantity: row.replyQuantity ?? 0,
            unitCost: row.replyUnitCost ?? 0,
            detailSubTotal: row.replyDetailSubTotal ?? null,
            replyQuantity: row.replyQuantity ?? null,
            replyUnitCost: row.replyUnitCost ?? null,
            replyDetailSubTotal: row.replyDetailSubTotal ?? null,
            replyDeliveryDate: row.replyDeliveryDate ?? null,
            shippingQuantity: 0,
            confirmFlg: false,
            confirmUserId: null,
            confirmDateTime: null,
            cancelOrderQuantity: null,
            cancelUserId: null,
            cancelDateTime: null,
            r3WbsLv2: null,
            r3OrderNo: null,
            r3ShippingDate: null,
            referenceDetailNo: null,
            isExpenses: false,
            isDeliveryCost: false,
          };
        });
    }
    function inpQuotationToOrder(
      orderDetails: OrderGetResponseSub[]
    ): OrderGetResponse {
      return {
        orderNo: 0,
        quotationNo: quotationNo ?? null,
        statusClassValue: OrderStatus.ORDERED,
        orderClassValue: inpQuotation.quotationClassValue ?? "",
        companyCode: inpQuotation.companyCode,
        companyName: inpQuotation.companyName ?? null,
        imoNo: inpQuotation.imoNo,
        vesselName: inpQuotation.vesselName ?? null,
        vesselBuilder: inpQuotation.vesselBuilder ?? null,
        yardNo: inpQuotation.yardNo ?? null,
        customerOrderNo1: null,
        customerOrderNo2: null,
        deliveryName: inpQuotation.deliveryName,
        deliveryPostalCode: inpQuotation.deliveryPostalCode,
        deliveryAddress: inpQuotation.deliveryAddress,
        deliveryTel: inpQuotation.deliveryTel,
        dispatchFlg: inpQuotation.dispatchFlg,
        dispatchPlace: inpQuotation.dispatchPlace ?? null,
        dispatchDate: inpQuotation.dispatchDate ?? null,
        orderUserId: inpQuotation.quotationUserId ?? 0,
        orderUserName: inpQuotation.quotationUserName ?? "",
        quotationCheckUserId: inpQuotation.checkUserId ?? 0,
        quotationCheckUserName: inpQuotation.checkUserName ?? "",
        orderComment: null,
        orderDateTime: null,
        replyUserId: null,
        replyUserName: null,
        replyComment: null,
        replyDateTime: null,
        replyDeliveryDate: null,
        replyInstallmentDeliveryFlg: inpQuotation.replyInstallmentDeliveryFlg,
        replySubtotalPrice: inpQuotation.replySubtotalPrice ?? null,
        replyDeliveryPrice: inpQuotation.replyDeliveryPrice ?? null,
        replyDiscountPrice: inpQuotation.replyDiscountPrice ?? null,
        replyTax: inpQuotation.replyTax ?? null,
        replyTaxPrice: inpQuotation.replyTaxPrice ?? null,
        replyTotalPrice: inpQuotation.replyTotalPrice ?? null,
        discountRate: inpQuotation.discountRate ?? null,
        checkUserId: null,
        checkDateTime: null,
        r3Wbs: null,
        r3ShippingDate: null,
        r3Comment: null,
        s3Prefix: inpQuotation.s3Prefix ?? null,
        delFlg: false,
        updateDateTime: null,
        requestReplyDeadline: inpQuotation.requestReplyDeadline ?? null,
        requestDeliveryDate: inpQuotation.requestDeliveryDate ?? null,
        quotationDateTime: inpQuotation.quotationDateTime ?? null,
        shippedComment: null,
        quotationComment: inpQuotation.quotationComment ?? null,
        dockName: inpQuotation.dockName ?? null,
        dockScheduleStart: inpQuotation.dockScheduleStart ?? null,
        dockScheduleEnd: inpQuotation.dockScheduleEnd ?? null,
        rejectUserId: null,
        rejectDateTime: null,
        problemProduct: inpQuotation.problemProduct ?? null,
        problemDetail: inpQuotation.problemDetail ?? null,
        compQuotationFlg: false,
        orderDetail: orderDetails,
        isCompTempSave: false,
        bemacFreeComment: inpQuotation.bemacFreeComment ?? null,
      };
    }

    function confirmMessageId(name: QuotationDetailSaveButtonName) {
      if (
        inpQuotation.quotationClassValue === OrderFactor.LCM_PARTS &&
        (name === "見積キャンセル" || name === "見積否認")
      ) {
        return "I057";
      } else if (
        inpQuotation.quotationClassValue === OrderFactor.LCM_RECOMMEND &&
        (name === "見積キャンセル" || name === "見積否認")
      ) {
        return "I067";
      } else {
        const messageIdMap: {
          name: QuotationDetailSaveButtonName;
          messageId: string;
        }[] = [
          { name: "見積依頼", messageId: "I009" },
          { name: "見積依頼変更", messageId: "I010" },
          { name: "見積回答", messageId: "I011" },
          { name: "再見積依頼", messageId: "I012" },
          { name: "見積キャンセル", messageId: "I013" },
          { name: "見積注文", messageId: "I014" },
          { name: "見積否認", messageId: "I015" },
        ];
        return messageIdMap.find((it) => it.name === name)?.messageId;
      }
    }

    function savedMessageId(name: QuotationDetailSaveButtonName) {
      const messageIdMap: {
        name: QuotationDetailSaveButtonName;
        messageId: string;
      }[] = [
        { name: "一時保存", messageId: "I045" },
        { name: "見積依頼", messageId: "I037" },
        { name: "見積依頼変更", messageId: "I038" },
        { name: "見積回答", messageId: "I039" },
        { name: "再見積依頼", messageId: "I040" },
        { name: "見積キャンセル", messageId: "I041" },
        { name: "見積注文", messageId: "I042" },
        { name: "見積否認", messageId: "I043" },
      ];
      return messageIdMap.find((it) => it.name === name)?.messageId;
    }

    // データ保存
    function saveData(
      buttonName: QuotationDetailSaveButtonName,
      orderParam?: OrderParam
    ) {
      // 添付ファイルを処理
      const uploadPromises = Promise.all([
        uploaderCustomerRef.current!.save(),
        uploaderBemacRef.current!.save(),
        uploaderFreeMemo.current?.save(),
      ]).then(() => {
        // 添付ファイルの処理が正常終了したら見積登録

        // 見積明細
        // 最終行の新規行を除いてリストを再生成
        let newFormRows = [...inpDetails]
          .filter((it) => !isEmptyQuotationRow(it))
          .map((it) => {
            const formRow: QuotationDetailFormRowValues = { ...it };
            // BEMACは依頼数量を入力できないので0で補完する
            if (auth.user().bemacFlg && !formRow.quantity) {
              formRow.quantity = 0;
              formRow.detailSubTotal = 0;
            }
            //品目コード=「---」の時は単価、数量を0で補完する
            if (
              formRow.productCode &&
              formRow.productCode === ProductCodeConst.BLANK
            ) {
              formRow.quantity = 0;
              formRow.standardUnitPrice = 0;
              formRow.replyQuantity = 0;
              formRow.replyUnitCost = 0;
            }

            // 見積依頼の場合は回答列に初期値セット
            // LCMの場合はLCMの回答内容のまま
            if (
              !isLcm() &&
              (buttonName === "見積依頼" || buttonName === "見積依頼変更") &&
              formRow.productCode !== ProductCodeConst.EXPENSES
            ) {
              formRow.replyUnitCost = formRow.standardUnitPrice;
              formRow.replyQuantity = formRow.quantity;
              formRow.replyDetailSubTotal =
                (formRow.standardUnitPrice ?? 0) * (formRow.quantity ?? 0);
            }
            return formRow;
          });

        //見積詳細番号を採番しなおす
        for (let i = 0; i < newFormRows.length; i++) {
          newFormRows[i].quotationDetailNo = i + 1;
        }

        const requestSubs: QuotationsPostRequestSub[] = newFormRows.map(
          (it) => {
            // リクエスト明細に変換
            return {
              quotationDetailNo: it.quotationDetailNo,
              productCode: it.productCode,
              productGroupName: it.productGroupName,
              productGroupNameEn: it.productGroupNameEn,
              spec: it.spec,
              unit: it.unit,
              standardUnitPrice: it.standardUnitPrice,
              note: it.note,
              quantity: it.quantity,
              detailSubTotal: it.detailSubTotal,
              replyUnitCost: it.replyUnitCost,
              replyQuantity: it.replyQuantity,
              replyDetailSubTotal: it.replyDetailSubTotal,
              replyDeliveryDate: it.replyDeliveryDate,
              referenceDetailNo: it.referenceDetailNo,
              createUserId: it.createUserId,
              createDateTime: it.createDateTime,
              updateUserId: it.updateUserId,
              updateDateTime: it.updateDateTime,
            };
          }
        );

        const request: QuotationsPostRequest = {
          ...inpQuotation,
          quotationNo: quotationNo,
          statusClassValue: getNextStatus(buttonName),
          lcmKeyCompanyCode: maintenanceScheduleKey
            ? maintenanceScheduleKey.companyCode
            : undefined,
          lcmKeyImoNo: maintenanceScheduleKey
            ? maintenanceScheduleKey.imoNo
            : undefined,
          lcmKeyInspectionYearClassValue: maintenanceScheduleKey
            ? maintenanceScheduleKey.inspectionYearClassValue
            : undefined,
          recommendKeyRecommendId: recommendKey
            ? recommendKey.recommendId
            : undefined,
          quotationsPostRequestSub: requestSubs,
          customerOrderNo1: undefined,
          customerOrderNo2: undefined,
        };

        if (orderParam) {
          request.customerQuotationNo1 = orderParam.customerQuotationNo1;
          request.customerQuotationNo2 = orderParam.customerQuotationNo2;
          request.customerOrderNo1 = orderParam.customerOrderNo1;
          request.customerOrderNo2 = orderParam.customerOrderNo2;
        }

        // 見積登録API実行
        return api
          .post(`/api/v1/quotations`, request)
          .then((response) => {
            const messageId = savedMessageId(buttonName);
            if (messageId) {
              splashMessage.show(messageId);
            }
            afterSaved(buttonName, response.data.quotationNo);
          })
          .catch((error) => {
            if (error.response.status === 404) {
              showDialog({ id: "E027" });
            } else {
              showDialog({ error });
              // 商品マスタの存在チェック
              checkProductCodes();
            }
          });
      });
      return uploadPromises;
    }

    /** 商品マスタの存在チェック */
    async function checkProductCodes() {
      // 注文明細から品目コードを重複なく取得する(空文字等は除く)
      const productCodes = Array.from(
        new Set(
          inpDetails
            .filter(
              (it) =>
                it.productCode.length > 0 &&
                it.productCode !== ProductCodeConst.BLANK
            )
            .map((it) => it.productCode)
        )
      );

      // 商品マスタから商品情報を取得する
      const notExistProductCodes: string[] = [];
      const promises = productCodes.map((productCode) =>
        api
          .get<ProductGetResponse>(`/api/v1/products/${productCode}`)
          .then(() => {})
          .catch((error) => {
            if (error.response.status === 404) {
              notExistProductCodes.push(productCode);
            } else {
              throw error;
            }
          })
      );
      await Promise.all(promises);
      // 注文明細で品目マスタに存在明細の存在フラグを落とす
      setInpDetails((prev) => {
        const rows = prev.map((row) => {
          row.existFlg = !notExistProductCodes.includes(row.productCode ?? "");
          return row;
        });
        return rows;
      });
    }

    function afterSaved(
      buttonName: QuotationDetailSaveButtonName,
      quotationNo: number
    ) {
      setFormChanged(false);

      if (buttonName === "一時保存") {
        setQuotationNo(quotationNo);

        api
          .get<QuotationGetResponse>(`/api/v1/quotations/${quotationNo}`)
          .then((response) => {
            const newQuotation = mapQuotationGetResponseToFormValues(
              response.data
            );
            const detailRows = mapQuotationGetResponseSubToFormRowValues(
              response.data.quotationDetail,
              true
            );
            // stateに保存してモーダルを表示状態にする
            saveInitialDataAndShow(newQuotation, detailRows);
          })
          .catch((error) => showDialog({ error }));
      } else {
        // 一時保存以外は画面を閉じる
        setShow(false);
      }
      if (props.onClickSaveButton) {
        props.onClickSaveButton(buttonName);
      }
    }

    function getNextStatus(name: QuotationDetailSaveButtonName) {
      switch (name) {
        case "見積依頼":
          return QuotationStatus.REQUESTED;
        case "見積回答":
          return QuotationStatus.ANSWERED;
        case "見積キャンセル":
          return QuotationStatus.DELETED;
        case "見積依頼変更":
          return QuotationStatus.REQUESTED;
        case "再見積依頼":
          return QuotationStatus.DENIED;
        case "見積否認":
          return QuotationStatus.DENIED;
        case "見積注文":
          return QuotationStatus.ACCEPTED;
        default:
          return inpQuotation.statusClassValue;
      }
    }

    // 注文詳細画面保存ボタンクリック
    function handleClickOrderDetailSaveButton(
      buttonName: string,
      retOrderParam?: OrderParam
    ) {
      // 保存処理
      saveData("見積注文", retOrderParam);
    }

    // OrderDetail共通コンポーネントエリアの変更イベント
    function handleChangeHeader(e: OrderDetailChangeEvent) {
      // 定価フラグの更新を受け取る
      if (e.name === "regularPriceFlg") {
        setCurrentRegularPriceFlg(e.value as boolean);
        return;
      }
      // 得意先コード変更時の処理
      if (e.name === "companyCode") {
        setCurrentRegularPriceFlg(e.regularPriceFlg ?? false);
        // 定価フラグの変更を伴う得意先変更があり、明細がある場合は確認ダイアログを表示
        if (e.regularPriceFlgChanged) {
          const hasDetails = inpDetails.some((detail) => detail.productCode);
          if (hasDetails) {
            // 確認ダイアログを表示
            showDialog({
              id: "I082",
            });
          }
        }
      }
      setFormChanged(true);

      // 項目名差異の吸収
      switch (e.name) {
        case "orderUserId":
          const quotationUserId = e.value as number | undefined;
          setInpQuotation((prev) => ({ ...prev, quotationUserId }));
          break;
        case "orderUserName":
          const quotationUserName = e.value as string | undefined;
          setInpQuotation((prev) => ({ ...prev, quotationUserName }));
          break;
        default:
          setInpQuotation((prev) => ({ ...prev, [e.name]: e.value }));
      }

      if (e.name === "dispatchFlg") {
        // 技術者派遣の場合
        if (e.value) {
          // トグルONになったら経費入力のためにスペーサー行を追加(BEMACユーザの見積)
          if (auth.user().bemacFlg && canAddExpensesRow(inpDetails)) {
            setInpDetails((prev) => {
              const rows = [...prev];
              // 最終行（空行のはず）にスペーサーをセット
              rows[rows.length - 1].productCode = ProductCodeConst.BLANK;
              // 空行を追加
              addNewRow(rows, [""]);
              return rows;
            });
            //経費行を追加
            addExpensesRow();
          }
        }
      }
      if (e.name === "replyDiscountPrice" || e.name === "replyTax") {
        // 割引額または税率の場合、合計金額再計算
        calculateReplyPrice(inpDetails);
      }
    }

    // テキストエリアの変更イベント
    function handleChangeTextArea(e: ChangeEvent<HTMLTextAreaElement>) {
      setFormChanged(true);
      setInpQuotation((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    // グリッドのセル値変更イベント
    const onCellValueChanged = (event: CellValueChangedEvent) => {
      setFormChanged(true);
      // 自身が最終行なら一行追加する
      if (event.rowIndex === inpDetails.length - 1 && canAddNewRow()) {
        setInpDetails((prev) => {
          const newRows = prev.concat();
          addNewRow(newRows);
          return newRows;
        });
      }
      // 品目コード変更時は商品単一取得API実行
      if (event.column.getColId() === "productCode") {
        const productCode = encodeURIComponent(event.value + "");
        if (productCode && productCode !== ProductCodeConst.BLANK) {
          const queryParams: string[] = [];
          queryParams.push(
            `onlyLcmCosts=${
              inpQuotation.quotationClassValue === OrderFactor.LCM_PARTS
                ? "true"
                : "false"
            }`
          );
          if (inpQuotation.companyCode) {
            queryParams.push(`companyCode=${inpQuotation.companyCode}`);
          }
          const query = queryParams.join("&");
          api
            .get<ProductGetResponse>(`/api/v1/products/${productCode}?${query}`)
            .then((response) => {
              setInpDetails((prev) => {
                const newRows = prev.map((it, index) => {
                  if (index === event.rowIndex) {
                    const data = response.data;
                    const newRow = prev[event.rowIndex ?? 0];
                    newRow.productGroupName = data.productGroupName;
                    newRow.productGroupNameEn = data.productGroupNameEn;
                    newRow.spec = data.spec;
                    newRow.standardUnitPrice = data.unitPrice ?? undefined;
                    newRow.unit = data.unit;
                    newRow.note = data.note;
                    newRow.detailSubTotal =
                      (data.unitPrice ?? 0) * (newRow.quantity ?? 0);
                    return newRow;
                  } else {
                    return it;
                  }
                });
                // 金額再計算
                calculateTempPrice(newRows);
                calculateReplyPrice(newRows);

                return newRows;
              });
            })
            .catch((error) => {
              if (error.response.status === 404) {
                showDialog({ id: "E029" });
              } else {
                showDialog({ error });
              }
            });
        } else {
          //品目コードクリア時、または「---」に変更時はレコードの値もクリアする
          setInpDetails((prev) => {
            const newRows = prev.map((it, index) => {
              if (index === event.rowIndex) {
                const newRow: QuotationDetailFormRowValues = {
                  productCode: productCode ?? "",
                  key: it.key,
                  isDbRow: it.isDbRow,
                  quotationDetailNo: it.quotationDetailNo,
                  addsByBemac: auth.user().bemacFlg,
                  existFlg: true,
                };
                return newRow;
              } else {
                return it;
              }
            });
            // 金額再計算
            calculateTempPrice(newRows);
            calculateReplyPrice(newRows);
            return newRows;
          });
        }
      }
      // 数量、単価変更時は仮見積金額再計算
      if (
        event.column.getColId() === "quantity" ||
        event.column.getColId() === "standardUnitPrice"
      ) {
        setInpDetails((prev) => {
          const newRows = prev.concat();
          newRows.forEach((row) => {
            row.detailSubTotal =
              (row.standardUnitPrice ?? 0) * (row.quantity ?? 0);
          });
          calculateTempPrice(newRows);
          return newRows;
        });
      }
      // BEMAC回答の数量、単価変更時は金額再計算
      if (
        event.column.getColId() === "replyQuantity" ||
        event.column.getColId() === "replyUnitCost"
      ) {
        setInpDetails((prev) => {
          const newRows = prev.concat();
          newRows.forEach((row) => {
            row.replyDetailSubTotal =
              (row.replyUnitCost ?? 0) * (row.replyQuantity ?? 0);
          });
          // 合計金額再計算
          calculateReplyPrice(newRows);
          return newRows;
        });
      }
    };

    // 合計金額再計算
    function calculateReplyPrice(gridRows: QuotationDetailFormRowValues[]) {
      setInpQuotation((prev) => {
        const price = calculateReplyPriceCore(
          gridRows,
          prev.replyDiscountPrice,
          prev.replyTax
        );

        return {
          ...prev,
          replySubtotalPrice: price.subtotalPrice,
          replyTaxPrice: price.taxPrice,
          replyTotalPrice: price.totalPrice,
        };
      });
    }

    // 仮見積金額再計算
    function calculateTempPrice(gridRows: QuotationDetailFormRowValues[]) {
      let subtotal = 0;
      gridRows.forEach((row) => {
        subtotal += row.detailSubTotal ?? 0;
      });
      setInpQuotation((prev) => ({ ...prev, tempSubtotalPrice: subtotal }));
    }

    function handleClickSearchParts() {
      // 明細の編集を停止
      gridRef.current?.api.stopEditing();
      // 商品一覧を表示
      productListModalRef.current?.show();
    }

    // 割引計算ボタン押下時の計算
    function handleClickCalculation() {
      const discountRate = inpQuotation.discountRate || 0;

      // 小計を取得
      let subtotal = 0;
      inpDetails.forEach((row) => {
        subtotal += row.replyDetailSubTotal ?? 0;
      });

      // 割引額を計算: 小計 × (割引率 / 100)
      const discountAmount = subtotal * (discountRate / 100);

      // 割引額を設定して金額再計算
      setInpQuotation((prev) => {
        const updatedCalculation = {
          ...prev,
          replyDiscountPrice: Math.floor(discountAmount), // 小数部切り捨て
        };

        // 合計金額再計算
        const subtotalMinusDiscount = subtotal - Math.floor(discountAmount);
        const taxPrice = calculateTax(subtotalMinusDiscount, prev.replyTax);
        const totalPrice = subtotalMinusDiscount + taxPrice;

        return {
          ...updatedCalculation,
          replySubtotalPrice: subtotal,
          replyTaxPrice: taxPrice,
          replyTotalPrice: totalPrice,
        };
      });
    }

    // 行ドラッグ
    const { onRowDragEnter, onRowDragEnd } =
      useDetailRowDrag<QuotationDetailFormRowValues>({
        gridRef,
        detailNoKey: "quotationDetailNo",
        canAddNewRow,
        addNewRow,
        setRows: setInpDetails,
        setFormChanged,
      });

    // 行削除
    function onClickDeleteButton(key: number) {
      setFormChanged(true);
      // 自身の行を削除し、その他の行のNoを採番しなおす
      const newRows = inpDetails
        .filter((it) => it.key !== key)
        .map((it, itIndex) => {
          it.quotationDetailNo = itIndex + 1;
          return it;
        });
      setInpDetails(newRows);
      // 金額再計算
      calculateTempPrice(newRows);
      calculateReplyPrice(newRows);
    }

    // 新規行追加
    function addNewRow(
      rows: QuotationDetailFormRowValues[],
      productCodes?: string[]
    ) {
      const getMaxKey = (rows: QuotationDetailFormRowValues[]) => {
        if (rows.length > 0) {
          return Math.max(...rows.map((it) => it.key));
        }
        return 0;
      };
      // 行を追加(複数も可)
      let maxKey = getMaxKey(rows);
      productCodes?.forEach((productCode) => {
        const newRow: QuotationDetailFormRowValues = {
          productCode: productCode,
          key: maxKey + 1,
          isDbRow: false,
          quotationDetailNo: rows.length + 1,
          addsByBemac: auth.user().bemacFlg,
          existFlg: true,
        };
        rows.push(newRow);
        maxKey++;
      });

      // 新規行を追加
      const blankRow: QuotationDetailFormRowValues = {
        productCode: "",
        key: maxKey + 1,
        isDbRow: false,
        quotationDetailNo: rows.length + 1,
        addsByBemac: auth.user().bemacFlg,
        existFlg: true,
      };
      rows.push(blankRow);
    }

    //経費行を追加する
    function addExpensesRow() {
      //経費
      const productCode = ProductCodeConst.EXPENSES;

      //商品単一取得API実行
      api
        .get<ProductGetResponse>(`/api/v1/products/${productCode}`)
        .then((response) => {
          setInpDetails((prev) => {
            const newRows = prev.map((it, index) => {
              if (index === prev.length - 2) {
                const data = response.data;
                const newRow = prev[prev.length ? prev.length - 2 : 0];
                newRow.productCode = data.productCode;
                newRow.productGroupName = data.productGroupName;
                newRow.productGroupNameEn = data.productGroupNameEn;
                newRow.spec = data.spec;
                newRow.standardUnitPrice = data.unitPrice ?? undefined;
                newRow.unit = data.unit;
                newRow.note = data.note;
                newRow.detailSubTotal =
                  (data.unitPrice ?? 0) * (newRow.quantity ?? 0);
                newRow.replyQuantity = 1;
                newRow.replyUnitCost = 0;
                return newRow;
              } else {
                return it;
              }
            });
            // 金額再計算
            calculateTempPrice(newRows);
            calculateReplyPrice(newRows);
            return newRows;
          });
        })
        .catch((error) => {
          if (error.response.status === 404) {
            showDialog({ id: "E029" });
          } else {
            showDialog({ error });
          }
        });
    }

    //準備費行を追加する
    function addReadyRow() {
      //経費
      const productCode = ProductCodeConst.READY;

      //商品単一取得API実行
      api
        .get<ProductGetResponse>(`/api/v1/products/${productCode}`)
        .then((response) => {
          setInpDetails((prev) => {
            const newRows = prev.map((it, index) => {
              if (index === prev.length - 3) {
                const data = response.data;
                const newRow = prev[prev.length ? prev.length - 3 : 0];
                newRow.productCode = data.productCode;
                newRow.productGroupName = data.productGroupName;
                newRow.productGroupNameEn = data.productGroupNameEn;
                newRow.spec = data.spec;
                newRow.standardUnitPrice = data.unitPrice ?? undefined;
                newRow.unit = data.unit;
                newRow.note = data.note;
                newRow.detailSubTotal =
                  (data.unitPrice ?? 0) * (newRow.quantity ?? 0);
                newRow.replyQuantity = 1;
                newRow.replyUnitCost = 0;
                return newRow;
              } else {
                return it;
              }
            });
            // 金額再計算
            calculateTempPrice(newRows);
            calculateReplyPrice(newRows);
            return newRows;
          });
        })
        .catch((error) => {
          if (error.response.status === 404) {
            showDialog({ id: "E029" });
          } else {
            showDialog({ error });
          }
        });
    }
    //準備費追加可否判定
    function canAddReadyRow(
      rows: QuotationDetailFormRowValues[],
      quotationClassValue?: string
    ) {
      return (
        rows.filter(
          (it) =>
            it.productCode === ProductCodeConst.READY &&
            quotationClassValue &&
            quotationClassValue === OrderFactor.LCM_PARTS
        ).length === 0
      );
    }

    // 商品一覧で部品を選択
    function handleSelectProducts(products: ProductsGetResponse[]) {
      setFormChanged(true);
      // 最終行の新規行を除いてリストを再生成
      const newInputDatas = [...inpDetails].filter(
        (it) =>
          it.quotationDetailNo !== inpDetails.length || !isEmptyQuotationRow(it)
      );
      // 選択された部品を追加
      let nextKey = Math.max(...inpDetails.map((it) => it.key)) + 1;
      let nextNo = newInputDatas.length + 1;
      const addDatas: QuotationDetailFormRowValues[] = products.map((it) => {
        return {
          key: nextKey++,
          isDbRow: false,
          quotationDetailNo: nextNo++,
          productCode: it.productCode,
          productGroupName: it.productGroupName,
          productGroupNameEn: it.productGroupNameEn,
          spec: it.spec,
          standardUnitPrice: it.unitPrice,
          quantity: auth.user().bemacFlg ? undefined : 1,
          unit: it.unit,
          note: it.note,
          detailSubTotal: it.unitPrice,
          replyUnitCost: auth.user().bemacFlg ? it.unitPrice : undefined,
          replyQuantity: auth.user().bemacFlg ? 1 : undefined,
          replyDetailSubTotal: auth.user().bemacFlg ? it.unitPrice : undefined,
          addsByBemac: auth.user().bemacFlg,
          existFlg: true,
        };
      });
      // 新規行を追加
      const emptyRow: QuotationDetailFormRowValues = {
        productCode: "",
        key: nextKey,
        isDbRow: false,
        quotationDetailNo: nextNo,
        addsByBemac: auth.user().bemacFlg,
        existFlg: true,
      };
      // stateに保存
      const newRows = [...newInputDatas, ...addDatas, emptyRow];
      setInpDetails(newRows);
      // 金額再計算
      calculateTempPrice(newRows);
      calculateReplyPrice(newRows);
    }

    // 見積書PDFボタンクリック
    function handleClickOutputPdf() {
      // 保存してなかったらエラー
      if (!quotationNo || formChanged) {
        showDialog({
          id: "E084",
          args: [tc("見積")],
        });
        return;
      }

      api
        .get(`/api/v1/quotation-pdf/${quotationNo}`, {
          responseType: "blob",
        })
        .then((it) => {
          const pdf = new Blob([it.data], { type: "application/pdf" });
          const pdfURL = URL.createObjectURL(pdf);
          window.open(pdfURL);
        })
        .catch((error) => {
          showDialog({ error });
        });
    }

    // グリッドの列定義
    const columnDefs = buildQuotationDetailColumnDefs({
      tc,
      permission,
      readOnly,
      bemacFlg: auth.user().bemacFlg,
      inpQuotation,
      onClickDeleteButton,
    });

    // モーダルbody部レンダリング
    function modalBody() {
      return (
        <>
          <OrderDetailHeader
            readOnly={readOnly}
            type={OrderDetailType.Quotation}
            status={inpQuotation.statusClassValue}
            orderFactor={inpQuotation.quotationClassValue ?? ""}
            quotationNo={quotationNo}
            quotationDateTime={inpQuotation.quotationDateTime ?? ""}
            replyUserId={inpQuotation.replyUserId ?? undefined}
            replyUserName={inpQuotation.replyUserName ?? ""}
            quotationCheckUserId={inpQuotation.checkUserId}
            quotationCheckUserName={inpQuotation.checkUserName}
            companyCode={inpQuotation.companyCode}
            companyName={inpQuotation.companyName ?? ""}
            orderUserId={inpQuotation.quotationUserId}
            orderUserName={inpQuotation.quotationUserName}
            vesselName={inpQuotation.vesselName ?? ""}
            imoNo={inpQuotation.imoNo ?? ""}
            vesselBuilder={inpQuotation.vesselBuilder ?? ""}
            yardNo={inpQuotation.yardNo ?? ""}
            customerQuotationNo1={inpQuotation.customerQuotationNo1 ?? ""}
            customerQuotationNo2={inpQuotation.customerQuotationNo2 ?? ""}
            requestDeliveryDate={inpQuotation.requestDeliveryDate ?? ""}
            requestReplyDeadline={inpQuotation.requestReplyDeadline ?? ""}
            dispatchFlg={inpQuotation.dispatchFlg}
            dispatchPlace={inpQuotation.dispatchPlace ?? ""}
            dispatchDate={inpQuotation.dispatchDate ?? ""}
            replyInstallmentDeliveryFlg={
              inpQuotation.replyInstallmentDeliveryFlg
            }
            dockName={inpQuotation.dockName ?? ""}
            dockScheduleStart={inpQuotation.dockScheduleStart ?? ""}
            dockScheduleEnd={inpQuotation.dockScheduleEnd ?? ""}
            problemProduct={inpQuotation.problemProduct ?? ""}
            problemDetail={inpQuotation.problemDetail ?? ""}
            onChange={handleChangeHeader}
            reQuotationFlg={inpQuotation.reQuotationFlg}
            compQuotationFlg={false}
            isCompTempSave={false}
            quotationOrderHistoryFlg={props.quotationOrderHistoryFlg}
          />
          <div>
            <div
              className="ag-theme-alpine w-100 mx-auto b-detail-items b-grid-outer b-header-row-white"
              data-cy="明細グリッド"
            >
              {showsUserCommentDraft() && (
                <span style={{ color: "red", fontSize: "16px" }}>
                  {tc(
                    "※品目等が分からない場合は、お客様備考へ記載してください。"
                  )}
                </span>
              )}
              <AgGridReact
                domLayout="autoHeight"
                defaultColDef={defaultColDef}
                columnDefs={columnDefs}
                rowData={inpDetails}
                onCellValueChanged={onCellValueChanged}
                singleClickEdit
                ref={gridRef}
                rowDragManaged={true}
                onRowDragEnter={onRowDragEnter}
                onRowDragEnd={onRowDragEnd}
                stopEditingWhenCellsLoseFocus={true}
              />
              <div className="row">
                <div className="col-4">
                  {auth.user().bemacFlg && (
                    <MdOutlineContentCopy
                      size="30px"
                      onClick={() => copyToClipboard()}
                      cursor="pointer"
                      style={{ marginTop: "30px" }}
                    />
                  )}
                </div>
                <div className="col-4">
                  <div className="text-center">
                    {canAddNewRow() && (
                      <input
                        type="button"
                        className="btn b-btn-primary"
                        value={tc("部品検索")}
                        onClick={handleClickSearchParts}
                        data-cy="部品検索ボタン"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-2">
              {/* 金額 */}
              <div className="col-4">
                {!isGeneralEngineer() && (
                  <OrderDetailPrice
                    readOnly={readOnly}
                    type={OrderDetailType.Quotation}
                    status={inpQuotation.statusClassValue}
                    replySubtotalPrice={inpQuotation.replySubtotalPrice}
                    replyDiscountPrice={inpQuotation.replyDiscountPrice}
                    replyTax={inpQuotation.replyTax}
                    replyTaxPrice={inpQuotation.replyTaxPrice}
                    replyTotalPrice={inpQuotation.replyTotalPrice}
                    onChange={handleChangeHeader}
                    reQuotationFlg={inpQuotation.reQuotationFlg}
                    showTempPrice={showsTempPrice()}
                    tempSubtotalPrice={inpQuotation.tempSubtotalPrice}
                  />
                )}
              </div>
              {/* 割引率入力欄と計算ボタン */}
              <div
                className="col-4"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: "35px",
                }}
              >
                {!isGeneralEngineer() &&
                  auth.user().bemacFlg &&
                  currentRegularPriceFlg && (
                    <OrderDetailDiscountRate
                      readOnly={readOnly}
                      type={OrderDetailType.Quotation}
                      status={inpQuotation.statusClassValue}
                      discountRate={inpQuotation.discountRate}
                      onChange={handleChangeHeader}
                      reQuotationFlg={inpQuotation.reQuotationFlg}
                      onCalculateDiscount={handleClickCalculation}
                    />
                  )}
              </div>
              {/* 送付先 */}
              <div className="col-4">
                <OrderDetailDelivery
                  readOnly={readOnly}
                  type={OrderDetailType.Quotation}
                  status={inpQuotation.statusClassValue}
                  deliveryName={inpQuotation.deliveryName}
                  deliveryPostalCode={inpQuotation.deliveryPostalCode}
                  deliveryAddress={inpQuotation.deliveryAddress}
                  deliveryTel={inpQuotation.deliveryTel}
                  bemacStartFlg={inpQuotation.bemacStartFlg}
                  reQuotationFlg={inpQuotation.reQuotationFlg}
                  onChange={handleChangeHeader}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-6">
                <div
                  className={`${areaClass} pt-2`}
                  data-cy="お客様添付ファイル"
                >
                  <div className="pb-1">
                    <span
                      className="b-input-group-text"
                      style={{ display: "inline" }}
                    >
                      {tc("お客様 添付ファイル")}
                    </span>
                    {editableFileUpload(false) && (
                      <span className="ms-2" style={{ color: "red" }}>
                        {tc("※ファイル添付は、最大10件までです。")}
                      </span>
                    )}
                  </div>
                  <S3Uploader
                    editable={editableFileUpload(false)}
                    maxCount={10}
                    ref={uploaderCustomerRef}
                  />
                </div>
              </div>
              {/* BEMAC：見積時の添付ファイル */}
              <div className="col-6">
                <div
                  className={`${areaClass} pt-2`}
                  data-cy="BEMAC添付ファイル"
                >
                  <div className="pb-1">
                    <span
                      className="b-input-group-text-light_blue"
                      style={{ display: "inline" }}
                    >
                      {tc("BEMAC 添付ファイル")}
                    </span>
                    {editableFileUpload(true) && (
                      <span className="ms-2" style={{ color: "red" }}>
                        {tc("※ファイル添付は、最大10件までです。")}
                      </span>
                    )}
                  </div>
                  <S3Uploader
                    editable={editableFileUpload(true)}
                    maxCount={10}
                    ref={uploaderBemacRef}
                  />
                </div>
              </div>
            </div>
            {/* 備考、コメント */}
            <div className="row">
              <div className="col-6">
                <div className={areaClass}>
                  {showsUserCommentDraft() && (
                    <span className="mt-2" style={{ color: "red" }}>
                      {tc(
                        "※品目等が分からない場合は、こちらへご記入ください。（過去の見積書を添付していただいても対応致します。）"
                      )}
                    </span>
                  )}
                  {showsUserCommentDenied() && (
                    <span className="mt-2" style={{ color: "red" }}>
                      {tc(
                        "※キャンセルや否認をされる際は、理由を備考にご記入ください。"
                      )}
                    </span>
                  )}
                  <div
                    className="b-input-group-text"
                    style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
                  >
                    {tc("お客様備考")}
                  </div>
                  <textarea
                    name="quotationComment"
                    className={textAreaClass}
                    disabled={!editableComment(false)}
                    value={inpQuotation.quotationComment}
                    maxLength={500}
                    onChange={handleChangeTextArea}
                    data-cy="お客様備考テキスト"
                  />
                </div>
              </div>
              <div className="col-6">
                <div className={areaClass}>
                  <div>
                    <div
                      className="b-input-group-text-light_blue"
                      style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
                    >
                      {tc("BEMACコメント")}
                    </div>
                    <textarea
                      name="replyComment"
                      className={textAreaClass}
                      disabled={!editableComment(true)}
                      value={inpQuotation.replyComment}
                      maxLength={500}
                      onChange={handleChangeTextArea}
                      data-cy="BEMACコメントテキスト"
                    />
                  </div>
                  {auth.user().bemacFlg && (
                    <div className="mt-3">
                      <div
                        className="b-input-group-text-light_blue"
                        style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
                      >
                        {tc("フリーメモ")}
                      </div>
                      <textarea
                        name="bemacFreeComment"
                        className={textAreaClass}
                        disabled={!editableComment(true)}
                        value={inpQuotation.bemacFreeComment}
                        maxLength={400}
                        onChange={handleChangeTextArea}
                        data-cy="フリーメモテキスト"
                      />
                    </div>
                  )}
                  {auth.user().bemacFlg && (
                    <div
                      className={`${areaClass} pt-2`}
                      data-cy="BEMACフリーメモ添付ファイル"
                    >
                      <div className="pb-1">
                        <span
                          className="b-input-group-text-light_blue"
                          style={{ display: "inline" }}
                        >
                          {tc("BEMAC 添付ファイル")}
                        </span>
                        {editableComment(true) && (
                          <span className="ms-2" style={{ color: "red" }}>
                            {tc("※ファイル添付は、最大10件までです。")}
                          </span>
                        )}
                      </div>
                      <S3Uploader
                        editable={editableComment(true)}
                        maxCount={10}
                        ref={uploaderFreeMemo}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <ProductListModal
            onDecide={handleSelectProducts}
            onlyLcmCosts={
              inpQuotation.quotationClassValue === OrderFactor.LCM_PARTS
            }
            ref={productListModalRef}
            companyCode={inpQuotation.companyCode}
          />
          <OrderDetail
            ref={orderDetailRef}
            onClickSaveButton={handleClickOrderDetailSaveButton}
            fromQuotationDetail={true}
          />
        </>
      );
    }

    // レンダリング
    return (
      <Modal
        fullscreen={true}
        show={show}
        onShow={handleShow}
        onHide={handleClose}
        scrollable
        data-cy="見積詳細モーダル"
      >
        <Modal.Header closeButton>
          <Modal.Title>{tc("見積詳細")}</Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="b-modal-detail"
          style={{ padding: "0px 12px 0px 12px" }}
        >
          {modalBody()}
        </Modal.Body>
        <Modal.Footer className="b-modal-detail">
          <QuotationDetailFooter
            readOnly={readOnly}
            statusClassValue={inpQuotation.statusClassValue}
            onClickAction={handleClickActionButton}
            onClickOutputPdf={handleClickOutputPdf}
            onClose={handleClose}
          />
        </Modal.Footer>
      </Modal>
    );
  }
);

export default QuotationDetail;
